import { useCallback, useEffect, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { LoadingOverlay, Box, Menu } from '@mantine/core'
import { RingProgress, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { Applicant, Step } from 'types/graphql'

import { navigate, routes, useParams } from '@redwoodjs/router'
import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import Button from 'src/components/Buttons/Button/Button'
import ToggleButtonGroup, {
  ToggleOption,
} from 'src/components/Inputs/ToggleButtonGroup/ToggleButtonGroup'
import { SearchBox } from 'src/components/Inputs/SearchBox/SearchBox'
import PaginationArea from 'src/components/Navigation/PaginationArea/PaginationArea'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import DocumentPreview from 'src/components/Overlays/DocumentPreview/DocumentPreview'
import FiltersMenu from 'src/components/Overlays/FiltersMenu/FiltersMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_DOCUMENTS,
  DELETE_DOCUMENT,
  GET_DOCUMENT_DOWNLOAD_URL,
  SET_DOCUMENT_RENEWED,
} from 'src/graphql/documents.graphql'
import {
  GET_SIDEBAR_CHECKLIST_STEPS,
  GET_STEPS_GROUP_BY_TYPE,
} from 'src/graphql/steps.graphql'
import { DQF_STEP_TYPES, StepStatus, StepType } from 'src/graphql/types/steps'
import { stepTypeToDocumentCategoryMapping } from 'src/graphql/types/steps'
import { usePage } from 'src/hooks/usePage/usePage'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconDocumentSearch from 'src/icons/IconDocumentSearch'
import IconFileCabinet from 'src/icons/IconFileCabinet'
import { isAdmin } from 'src/lib/auth/permissions'
import { buildDocumentCategoryOptions } from 'src/lib/documentCategory.utils'
import { formatDocumentCategory } from 'src/lib/formatters'
import { sortStepsByTemplate } from 'src/lib/stepOrder'
import { downloadFile } from 'src/utils/downloadFile'

import ExpirationDetailDrawer from 'src/pages/ExpirationsPage/components/ExpirationDetailDrawer'

import DocumentsTable from './components/DocumentsTable/DocumentsTable'
import DQFContent from './components/DQFContent/DQFContent'
import EditDocumentModal from './components/EditDocumentModal/EditDocumentModal'
import ESignatureStatusDrawer from './components/ESignatureStatus/ESignatureStatusDrawer'
import ExportDocumentsModal from './components/ExportDocumentsModal/ExportDocumentsModal'
import SendDocumentsToSignModal from './components/SendDocumentsToSignModal/SendDocumentsToSignModal'
import UploadDocumentModal from './components/UploadDocumentModal/UploadDocumentModal'
import {
  buildPreviewFilterLabel,
  pageContainingIndex,
} from './hooks/documentPreviewNavigation'
import { useDocumentListNavigation } from './hooks/useDocumentListNavigation'
import { useResetOnSignal } from './hooks/useResetOnSignal'
import { subscribe } from 'src/lib/bulkUploadDocumentProgress'

const onError = () => {
  toast('Something went wrong, please try again.', 'error')
}

const STEPS_PAGE_INFO = { offset: 0, limit: 100 }

const DocumentsTab = ({
  applicantId,
  dqf,
  applicantInfo,
  tabReclickSignal = 0,
}: {
  applicantId: string
  dqf: StepType
  applicantInfo: Applicant
  // Bumped by the page each time the already-active Documents tab is clicked
  tabReclickSignal?: number
}) => {
  const params = useParams()
  const [isUploadDocumentModalOpen, setIsDocumentUploadModalOpen] =
    useState(false)
  const [documentToDelete, setDocumentToDelete] = useState(null)
  const [documentToEdit, setDocumentToEdit] = useState(null)
  const [page, setPage] = usePage()
  const [documentForPreview, setDocumentForPreview] = useState(null)
  // Default to 'documents' subtab if documentId is in URL (coming from dashboard expirations)
  const [selectedSubTab, setSelectedSubTab] = useState(
    params?.subtab || (params?.documentId ? 'documents' : 'DQF')
  )
  const [DQFCompletion, setDQFCompletion] = useState(0)
  const [selectedStep, setSelectedStep] = useState(null)
  const [openSendDocumentsToSignModal, setOpenSendDocumentsToSignModal] =
    useState(false)
  const { currentUser } = useAuth()
  // Admin only for now; the resolver enforces it, this only hides the entry point
  const canSeeESignatureStatus = isAdmin(currentUser)
  const [
    eSignatureStatusOpened,
    { open: openESignatureStatus, close: closeESignatureStatus },
  ] = useDisclosure(false)
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(
    new Set()
  )
  const [selectedDocumentDetails, setSelectedDocumentDetails] = useState<
    Map<string, any>
  >(new Map())
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [limit, setLimit] = useState(20)
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const categoryOptions = buildDocumentCategoryOptions(formatDocumentCategory)
  const [trackerDrawerOpened, { open: openTrackerDrawer, close: closeTrackerDrawer }] = useDisclosure(false)
  const [selectedTrackerId, setSelectedTrackerId] = useState<string | null>(null)
  const [isUploadInProgress, setIsUploadInProgress] = useState(false)

  const handleOpenTracker = (trackerId: string) => {
    setSelectedTrackerId(trackerId)
    openTrackerDrawer()
  }

  // Subscribe to bulk upload progress to disable upload button while uploading
  useEffect(() => {
    const unsubscribe = subscribe((progress) => {
      setIsUploadInProgress(progress.isActive)
    })
    return unsubscribe
  }, [])

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit)
    setPage(1) // Reset to first page when changing limit
  }

  // Immediate search when pressing Enter
  const handleSearch = (value: string) => {
    setAppliedSearchTerm(value)
    setPage(1)
  }

  // Debounced search - automatically search after user stops typing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm !== appliedSearchTerm) {
        setAppliedSearchTerm(searchTerm)
        setPage(1) // Reset to first page when searching
      }
    }, 300) // 300ms debounce delay

    return () => clearTimeout(debounceTimer)
  }, [searchTerm])

  useEffect(() => {
    setSelectedSubTab(params?.subtab || 'DQF')
  }, [params.subtab])

  const dqfFilterForStep = (step) => {
    if (!step) return {}
    if (step.customStepId) return { stepId: step.stepId }
    const category = stepTypeToDocumentCategoryMapping[step.type]
    return category ? { categories: [category] } : {}
  }

  // Filters active on the Documents subtab (search + category). Built once and
  // reused for both the active query and refetchQueries so Apollo keys both to
  // the same cache slot (see refetchQueries comment below).
  const documentsFilters = {
    ...(appliedSearchTerm && { searchTerm: appliedSearchTerm }),
    ...(selectedCategories.length > 0 && { categories: selectedCategories }),
  }
  const hasDocumentsFilters = Object.keys(documentsFilters).length > 0

  // Previous/next in the preview follows the list the user was looking at: the
  // Documents subtab's search/category filters, or every document when the
  // preview was opened from the checklist or a link.
  const previewDocumentId = documentForPreview?.documentId ?? null
  const previewFilters = selectedSubTab === 'documents' ? documentsFilters : {}
  const previewList = useDocumentListNavigation({
    applicantId,
    documentId: previewDocumentId,
    filters: previewFilters,
    skip: !previewDocumentId,
  })

  const documentsQueryVariables = {
    applicantId,
    pageInfo: { offset: (page - 1) * limit, limit: limit },
    ...(hasDocumentsFilters && {
      filters: documentsFilters,
    }),
  }

  // Apollo keys cached query results by their variables object. The refetch
  // variables below must produce the EXACT same shape that the active query
  // used at call time — otherwise the refetch writes to a different cache
  // slot and the UI keeps reading stale data. In particular: only emit a
  // `filters` key when there's a real filter to apply (DQF category on the
  // DQF subtab, or searchTerm on the documents subtab). Matches
  // `documentsQueryVariables` above and `handleStepChange` below.
  //TODO: Refactor to refresh only when necessary, not when selecting tabs
  const refetchQueries = [
    {
      query: GET_DOCUMENTS,
      variables: {
        applicantId,
        pageInfo: { offset: (page - 1) * limit, limit: limit },
        ...(selectedSubTab === 'DQF' && {
          filters: dqfFilterForStep(selectedStep),
        }),
        ...(selectedSubTab === 'documents' &&
          hasDocumentsFilters && {
          filters: documentsFilters,
        }),
      },
    },
    {
      query: GET_STEPS_GROUP_BY_TYPE,
      variables: {
        applicantId,
        pageInfo: STEPS_PAGE_INFO,
        stepTypes: [...DQF_STEP_TYPES, 'CUSTOM' as StepType],
      },
    },
    // Document edits can move documents between checklist items and roll step
    // statuses up/down — the applicant sidebar renders the same checklist from
    // its own query, so refresh it too (same pattern as OrderDetails).
    {
      query: GET_SIDEBAR_CHECKLIST_STEPS,
      variables: { applicantId },
    },
  ]

  const [
    loadDocuments,
    { data: documentsResponse, loading: loadingDocuments },
  ] = useLazyQuery(GET_DOCUMENTS, {
    onError,
  })

  const [loadSteps, { data: stepsResponse, loading: loadingDFQ }] =
    useLazyQuery(GET_STEPS_GROUP_BY_TYPE, {
      variables: {
        applicantId,
        pageInfo: STEPS_PAGE_INFO,
        stepTypes: [...DQF_STEP_TYPES, 'CUSTOM' as StepType],
      },
      onError,
    })

  const orderedSteps = sortStepsByTemplate(
    stepsResponse?.stepsGroupByType?.items ?? [],
    applicantInfo?.jobListing
  )
  const firstStep =
    (dqf &&
      orderedSteps.find(
        (item) => (item.customStepId ?? item.type) === dqf
      )) ||
    orderedSteps[0] ||
    null

  const stepItems = stepsResponse?.stepsGroupByType?.items ?? []
  const stepItemsKey = stepItems
    .map((s) => s?.customStepId ?? s?.type)
    .join(',')

  useEffect(() => {
    const keyOf = (s) => s?.customStepId ?? s?.type
    const selectedStillPresent =
      selectedStep && stepItems.some((s) => keyOf(s) === keyOf(selectedStep))

    if (!selectedStillPresent) {
      setSelectedStep(firstStep)
      if (firstStep) handleStepChange(firstStep)
    }
  }, [stepItemsKey])

  useEffect(() => {
    if (selectedSubTab === 'documents') {
      loadDocuments({ variables: documentsQueryVariables })
    }
  }, [
    params,
    loadDocuments,
    applicantId,
    page,
    limit,
    appliedSearchTerm,
    selectedCategories,
  ])

  const handleCategoriesChange = (categories: string[]) => {
    setSelectedCategories(categories)
    setPage(1)
  }

  useEffect(() => {
    const newStep = stepsResponse?.stepsGroupByType?.items.find(
      (step) => (step.customStepId ?? step.type) === dqf
    )
    if (newStep) {
      setSelectedStep(newStep)
      handleStepChange(newStep)
    }
  }, [dqf])

  useEffect(() => {
    if (selectedSubTab === 'DQF') {
      loadSteps()
      // When switching to DQF, ensure we have a selected step
      const stepToSelect = selectedStep || firstStep
      if (stepToSelect) {
        setSelectedStep(stepToSelect)
        // Always load documents with filter when switching to DQF
        handleStepChange(stepToSelect)
      }
    } else {
      setSelectedStep(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubTab])

  const [deleteDocument] = useMutation(DELETE_DOCUMENT, {
    refetchQueries,
    onCompleted: () => {
      setDocumentToDelete(null)
      if (documentsResponse?.documents.totalCount % limit === 1 && page > 1) {
        setPage(page - 1)
      }
      toast('Document deleted successfully', 'success')
    },
    onError,
  })

  const [setDocumentRenewed] = useMutation(SET_DOCUMENT_RENEWED, {
    refetchQueries,
    onCompleted: (res) => {
      toast(
        `Document marked as ${res.setDocumentRenewed.isRenewed ? 'renewed' : 'not renewed'
        }`,
        'success'
      )
    },
    onError,
  })

  const [downloadDocument, { called, data: downloadResponse }] = useLazyQuery(
    GET_DOCUMENT_DOWNLOAD_URL,
    {
      onCompleted: () => {
        if (called && downloadResponse?.documentDownloadUrl?.url) {
          const { url, fileName, fileType } = downloadResponse.documentDownloadUrl
          downloadFile({ url, fileName, fileType })
        }
      },
      onError,
    }
  )

  const handleDelete = (documentId) => {
    deleteDocument({
      variables: {
        id: documentId,
        applicantId,
      },
    })
  }

  const handleDeleteConfirmation = (documentId) => {
    setDocumentToDelete(documentId)
  }

  const handleEditDocument = (document) => {
    setDocumentToEdit(document)
  }

  const handleDownload = (documentId) => {
    downloadDocument({
      variables: {
        id: documentId,
        applicantId,
      },
    })
  }

  const handlePreview = (document) => {
    setDocumentForPreview(document)
  }

  const handleUpload = () => {
    setIsDocumentUploadModalOpen(true)
  }

  const handleUploadComplete = () => {
    loadSteps()

    if (selectedSubTab === 'documents') {
      loadDocuments({ variables: documentsQueryVariables })
    } else if (selectedStep) {
      handleStepChange(selectedStep)
    }
  }

  const handleStepChange = (step: Step) => {
    loadDocuments({
      variables: {
        applicantId,
        pageInfo: { offset: (page - 1) * limit, limit: limit },
        ...(selectedSubTab === 'DQF' && {
          filters: dqfFilterForStep(step),
        }),
      },
    })
  }

  const handleDocumentRenewed = (documentId, isRenewed) => {
    setDocumentRenewed({
      variables: {
        documentId,
        isRenewed,
        applicantId,
      },
    })
  }

  const handleSelectDocument = (documentId: string, checked: boolean) => {
    setSelectedDocuments((prev) => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(documentId)
        // Store document details
        const document = documentsResponse?.documents.items.find(
          (doc) => doc.documentId === documentId
        )
        if (document) {
          setSelectedDocumentDetails((prevDetails) => {
            const newMap = new Map(prevDetails)
            newMap.set(documentId, document)
            return newMap
          })
        }
      } else {
        newSet.delete(documentId)
        // Remove from details map
        setSelectedDocumentDetails((prevDetails) => {
          const newMap = new Map(prevDetails)
          newMap.delete(documentId)
          return newMap
        })
      }
      return newSet
    })
  }

  const handleSelectAll = (checked: boolean) => {
    const documents = documentsResponse?.documents.items || []
    if (checked) {
      // Add all current page documents to selection
      const allDocumentIds = documents.map((doc) => doc.documentId)
      setSelectedDocuments((prev) => {
        const newSet = new Set(prev)
        allDocumentIds.forEach((id) => newSet.add(id))
        return newSet
      })
      // Store all document details
      setSelectedDocumentDetails((prev) => {
        const newMap = new Map(prev)
        documents.forEach((doc) => newMap.set(doc.documentId, doc))
        return newMap
      })
    } else {
      // Remove only current page documents from selection
      const currentPageIds = documents.map((doc) => doc.documentId)
      setSelectedDocuments((prev) => {
        const newSet = new Set(prev)
        currentPageIds.forEach((id) => newSet.delete(id))
        return newSet
      })
      // Remove current page document details
      setSelectedDocumentDetails((prev) => {
        const newMap = new Map(prev)
        currentPageIds.forEach((id) => newMap.delete(id))
        return newMap
      })
    }
  }

  const handleExportClick = () => {
    setIsExportModalOpen(true)
  }

  const onOpenSendDocumentSigningModal = useCallback(() => {
    setOpenSendDocumentsToSignModal(true)
  }, [])

  const subtabOptions: ToggleOption[] = [
    { value: 'DQF', label: 'Checklist', icon: <IconDocumentSearch /> },
    { value: 'documents', label: 'Documents', icon: <IconFileCabinet /> },
  ]

  const handleSubTabChange = (tab: string) => {
    navigate(
      routes.applicantDetails({
        id: applicantId,
        tab: 'documents',
        subtab: tab,
      })
    )
    if (tab === 'documents') {
      setSearchTerm('')
      setAppliedSearchTerm('')
      setSelectedCategories([])
      loadDocuments({
        variables: {
          applicantId,
          pageInfo: { offset: (page - 1) * limit, limit: limit },
        },
      })
    }
  }

  useEffect(() => {
    if (stepsResponse?.stepsGroupByType) {
      const { items } = stepsResponse.stepsGroupByType
      const totalSteps = items.length
      const completedSteps = items.filter(
        (step) => step.status === StepStatus.COMPLETED
      ).length
      const completion = (completedSteps / totalSteps) * 100
      setDQFCompletion(completion)
    }
  }, [stepsResponse])

  // Auto-open document preview when documentId is in URL params (e.g., from dashboard expirations,
  // license card review flags). DocumentPreview fetches its own data by documentId, so we don't
  // need the full document object from the paginated list — just the id.
  useEffect(() => {
    const documentIdFromUrl = params?.documentId
    if (documentIdFromUrl && !documentForPreview) {
      setDocumentForPreview({ documentId: documentIdFromUrl })
    }
  }, [params?.documentId, documentForPreview])

  const goToPreviewDocument = (id?: string) => {
    if (id) setDocumentForPreview({ documentId: id })
  }

  // Clearing the filter from inside the preview clears it for real, so the
  // list is unfiltered when the user goes back to it.
  const handleClearPreviewFilters = () => {
    setSearchTerm('')
    setAppliedSearchTerm('')
    setSelectedCategories([])
  }

  const previewFilterLabel = buildPreviewFilterLabel(previewFilters)
  const previewNavigation =
    previewDocumentId && previewList.loading
      ? { position: 0, total: 0, loading: true }
      : previewList.index >= 0 && previewList.total > 1
      ? {
          position: previewList.index + 1,
          total: previewList.total,
          onPrevious: previewList.previousId
            ? () => goToPreviewDocument(previewList.previousId)
            : undefined,
          onNext: previewList.nextId
            ? () => goToPreviewDocument(previewList.nextId)
            : undefined,
          filterLabel: previewFilterLabel,
          onClearFilter: previewFilterLabel
            ? handleClearPreviewFilters
            : undefined,
        }
      : undefined

  // Clicking the Documents tab while a document is open leaves the document
  // and lands on the checklist overview (the tab's usual landing page).
  useResetOnSignal(tabReclickSignal, !!documentForPreview, () => {
    setDocumentForPreview(null)
    setSelectedSubTab('DQF')
    if (params?.documentId || params?.subtab) {
      navigate(routes.applicantDetails({ id: applicantId, tab: 'documents' }), {
        replace: true,
      })
    }
  })

  // Handle closing the document preview and clearing the documentId from URL.
  // After stepping through documents the table returns to the page holding the
  // document the user last viewed, not the page they started on.
  const handleClosePreview = useCallback(() => {
    const targetPage =
      selectedSubTab === 'documents' && previewList.index >= 0
        ? pageContainingIndex(previewList.index, limit)
        : null
    setDocumentForPreview(null)
    // Clear the documentId from URL when closing preview
    if (params?.documentId) {
      navigate(
        routes.applicantDetails({
          id: applicantId,
          tab: 'documents',
          subtab: 'documents',
          ...(targetPage > 1 && { page: targetPage }),
        }),
        { replace: true }
      )
    } else if (targetPage) {
      setPage(targetPage)
    }
  }, [
    applicantId,
    params?.documentId,
    selectedSubTab,
    previewList.index,
    limit,
    setPage,
  ])

  return (
    <div className="flex h-[90%] flex-col gap-4 overflow-auto">
      {documentForPreview ? (
        <DocumentPreview
          // Remount per document so rotation, zoom and rename drafts don't
          // carry over when stepping to the next one
          key={documentForPreview.documentId}
          documentId={documentForPreview.documentId}
          applicantId={applicantId}
          onBack={handleClosePreview}
          refetchQueries={refetchQueries}
          navigation={previewNavigation}
        />
      ) : (
        <>
          <div className="relative flex flex-row items-center justify-between gap-4">
            <div className="flex flex-row items-center gap-2">
              <div className="flex flex-col">
                <div className="text-lg font-semibold text-doubleNickel-black">
                  {selectedSubTab === 'DQF' ? 'Checklist' : 'Documents'}
                </div>
                <div className="text-sm text-doubleNickel-gray-600">
                  <span>
                    {selectedSubTab === 'DQF'
                      ? stepsResponse?.stepsGroupByType.totalCount
                      : documentsResponse?.documents.totalCount}
                  </span>
                  <span>
                    {selectedSubTab === 'DQF' ? ' steps' : ' document(s)'}
                  </span>
                </div>
              </div>
              {selectedSubTab === 'DQF' && DQFCompletion !== null && (
                <div className="flex flex-row items-center gap-1">
                  <RingProgress
                    sections={[{ value: Number(DQFCompletion), color: 'blue' }]}
                    size={55}
                    roundCaps
                    thickness={5}
                    label={
                      <Text className="flex items-center justify-center text-xs font-medium text-doubleNickel-gray-600">
                        {DQFCompletion.toFixed(0)}%
                      </Text>
                    }
                  />
                  <Text className="text-xs font-medium text-doubleNickel-gray-600">
                    Checklist Completion
                  </Text>
                </div>
              )}
            </div>
            <div className="flex flex-row items-center gap-2">
              {selectedSubTab === 'documents' && (
                <>
                  <div className="w-56">
                    <SearchBox
                      value={searchTerm}
                      setValue={setSearchTerm}
                      placeholder="Search by file name..."
                      onSearch={handleSearch}
                    />
                  </div>
                  <FiltersMenu
                    filters={[
                      {
                        title: 'Category',
                        selected: selectedCategories,
                        onChange: handleCategoriesChange,
                        options: categoryOptions,
                      },
                    ]}
                  />
                </>
              )}

              <ToggleButtonGroup
                options={subtabOptions}
                value={selectedSubTab}
                onChange={handleSubTabChange}
              />

              <Menu shadow="md" position="bottom-end">
                <Menu.Target>
                  <Box className="font-lg flex flex-row items-center justify-between font-bold text-doubleNickel-gray-700">
                    <Button
                      text="Actions"
                      righticon={
                        <IconChevronDown className="h-3 fill-none stroke-doubleNickel-gray-600" />
                      }
                    />
                  </Box>
                </Menu.Target>
                <Menu.Dropdown className="w-40">
                  {selectedSubTab === 'documents' && (
                    <>
                      <Menu.Item
                        className={` text-doubleNickel-gray-700`}
                        onClick={() => setIsDocumentUploadModalOpen(true)}
                        disabled={isUploadInProgress}
                      >
                        Upload
                      </Menu.Item>
                      <Menu.Item
                        className="text-doubleNickel-gray-700"
                        onClick={handleExportClick}
                        disabled={selectedDocuments.size === 0}
                      >
                        Export Documents ({selectedDocuments.size})
                      </Menu.Item>
                    </>
                  )}
                  <Menu.Item
                    className="text-doubleNickel-gray-700"
                    onClick={() => onOpenSendDocumentSigningModal()}
                  >
                    Send Documents for Signing
                  </Menu.Item>
                  {canSeeESignatureStatus && (
                    <Menu.Item
                      className="text-doubleNickel-gray-700"
                      onClick={openESignatureStatus}
                    >
                      E-Signature Status
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            </div>
          </div>
          {selectedSubTab === 'documents' && (
            <>
              <Box
                className="flex h-full flex-col overflow-auto"
                pos={'relative'}
              >
                <DocumentsTable
                  documents={documentsResponse?.documents.items}
                  loading={loadingDocuments}
                  onEdit={handleEditDocument}
                  onDownload={handleDownload}
                  onPreview={handlePreview}
                  onDelete={handleDeleteConfirmation}
                  onRenewed={handleDocumentRenewed}
                  refetchQueries={refetchQueries}
                  selectedDocuments={selectedDocuments}
                  onSelectDocument={handleSelectDocument}
                  onSelectAll={handleSelectAll}
                  onOpenTracker={handleOpenTracker}
                  applicantInfo={applicantInfo}
                />
                <LoadingOverlay
                  visible={loadingDocuments}
                  overlayProps={{ opacity: 0 }}
                  zIndex={1000}
                />
              </Box>
              <PaginationArea
                page={page}
                totalPages={Math.ceil(
                  documentsResponse?.documents.totalCount / limit
                )}
                onPageChange={setPage}
                resultsPerPage={limit}
                setResultsPerPage={handleLimitChange}
              />
            </>
          )}
          {selectedSubTab === 'DQF' && stepsResponse && selectedStep && (
            <DQFContent
              steps={stepsResponse?.stepsGroupByType}
              documentsResponse={documentsResponse?.documents.items}
              handleUpload={handleUpload}
              onDelete={handleDeleteConfirmation}
              onEdit={handleEditDocument}
              onPreview={handlePreview}
              onDownload={handleDownload}
              onStepChange={handleStepChange}
              onRenewed={handleDocumentRenewed}
              refetchQueries={refetchQueries}
              onError={onError}
              applicantId={applicantId}
              applicantInfo={applicantInfo}
              selected={selectedStep}
              setSelected={setSelectedStep}
              onOpenTracker={handleOpenTracker}
              isUploadInProgress={isUploadInProgress}
            />
          )}
        </>
      )}
      {isUploadDocumentModalOpen && (
        <UploadDocumentModal
          step={selectedStep}
          applicantId={applicantId}
          isOpen={isUploadDocumentModalOpen}
          onRender={refetchQueries}
          onClose={() => setIsDocumentUploadModalOpen(false)}
          onUpdateComplete={handleUploadComplete}
          voe={null}
          disableCategorySelection={selectedSubTab === 'DQF'}
        />
      )}
      {documentToEdit && (
        <EditDocumentModal
          document={documentToEdit}
          applicantId={applicantId}
          isOpen={Boolean(documentToEdit)}
          onRender={refetchQueries}
          onClose={() => {
            setDocumentToEdit(null)
          }}
        />
      )}
      {documentToDelete && (
        <DeleteModal
          title="Are you sure you want to delete this document?"
          opened={Boolean(documentToDelete)}
          close={() => setDocumentToDelete(null)}
          onDelete={() => handleDelete(documentToDelete)}
        />
      )}
      {canSeeESignatureStatus && (
        <ESignatureStatusDrawer
          applicantId={applicantId}
          opened={eSignatureStatusOpened}
          onClose={closeESignatureStatus}
        />
      )}
      {openSendDocumentsToSignModal && (
        <SendDocumentsToSignModal
          email={applicantInfo.email}
          isOpen={openSendDocumentsToSignModal}
          onClose={() => setOpenSendDocumentsToSignModal(false)}
          applicantId={applicantId}
        />
      )}
      {isExportModalOpen && (
        <ExportDocumentsModal
          isOpen={isExportModalOpen}
          onClose={() => {
            setIsExportModalOpen(false)
            setSelectedDocuments(new Set())
            setSelectedDocumentDetails(new Map())
          }}
          applicantId={applicantId}
          selectedDocumentIds={Array.from(selectedDocuments)}
          selectedDocuments={Array.from(selectedDocumentDetails.values())}
        />
      )}
      {trackerDrawerOpened && (
        <ExpirationDetailDrawer
          opened={trackerDrawerOpened}
          onClose={closeTrackerDrawer}
          trackerId={selectedTrackerId}
        />
      )}
    </div>
  )
}

export default DocumentsTab
